// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SavePopup.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import Label from 'share/components/controls/Izenda.Label';
import Constants from 'logic/data/Izenda.ReportDesignerConstants';
import DashboardConstants from 'logic/data/Izenda.DashboardConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import uiState from 'logic/Izenda.UiState';
import actions from './Izenda.SavePopupActions';
import appStore from 'logic/Izenda.AppStore';
import { set, get, merge, flow, filter, head, isNil } from 'lodash/fp';

const ValidatorInput = Validator()(Input);

@connect(actions)
class SavePopup extends Component {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
    popupConfig: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired
  };

  componentDidMount() {
    const { reportName, reportType, categoryId, subCategoryId, isGlobal } = this.props.model;
    appStore.loadCategorySettings().then(result => {
      this.props.updateComponentState(set('localGlobalCategories', result));
    });

    this.props.updateOriginalState({
      reportName,
      reportType,
      categoryId,
      subCategoryId,
      isGlobal
    });
  }

  getSaveInCategory = (isGlobal: boolean, localGlobalCategories: any) => {
    return flow(filter({ isGlobal: isGlobal }), head)(localGlobalCategories);
  };

  getSelectedCategory = (model: any) => {
    return isNil(model.categoryId) ? null : { id: model.categoryId, name: model.categoryName };
  };

  getSelectedSubCategory = (model: any) => {
    return isNil(model.subCategoryId) ? null : { id: model.subCategoryId, name: model.subCategoryName };
  };

  changeReportName = (event) => {
    this.props.changeNameHandler(event.target.value);
  };

  onReportNameFocus = (event: FocusEvent) => {
    event.target.select();
  };

  onReportNameKeyPress = (event: KeyboardEvent) => {
    const close = () => this.props.hide();
    const closeThisModal = type => this.props.onClosing && this.props.onClosing(close, type);
    switch (event.key) {
      case 'Enter':
        closeThisModal('accept');
        break;
    }
  };

  /**
   * Check to display Save Into component.
   */
  doesShowSaveInto = () => isNil(uiState.tenantId) && !!uiState.currentUser.systemAdmin;

  renderContainer = () => {
    const props = this.props;
    const {
      model,
      componentState,
      authorization,
      authorizationConstants: { PERMISSION_KEY },
      localization,
      popupConfig,
      changeSaveInHandler,
      changeCategoryHandler,
      getCategories,
      searchCategoryDataHandler,
      resetCategory,
      changeSubCategoryHandler,
      getSubCategories,
      searchSubCategoryDataHandler,
      resetSubCategory,
      isDashboard
    } = props;

    // process to create temp category type id
    const selectedSaveInto = get('id', this.getSaveInCategory(model.isGlobal, componentState.localGlobalCategories));
    const hasPrivilege = authorization.checkPrivileges([PERMISSION_KEY.DashboardsCanCreateNewCategoryValue]);

    const selectedCategory = this.getSelectedCategory(model);
    const selectedSubCategory = this.getSelectedSubCategory(model);

    let selectCategoryHint = localization.getText('REPORTDESIGNER_MESSAGE_HINT_CATEGORY');
    let selectSubCategoryHint = localization.getText('REPORTDESIGNER_MESSAGE_HINT_SUB_CATEGORY');
    let addNewCategory = props.addNewCategoryHandler;
    let addNewSubCategory = props.addNewSubCategoryHandler;
    if (!hasPrivilege) {
      addNewCategory = () => false;
      selectCategoryHint = null;
      addNewSubCategory = () => false;
      selectSubCategoryHint = null;
    }

    const reportNameVals = merge(
      Constants.CONSTRAINTS.REPORT_NAME,
      {
        action: function() {
          return props.validate;
        }
      },
      {}
    );

    const nameValidation = isDashboard ? DashboardConstants.CONSTRAINTS.NAME : reportNameVals;

    return (
      <GridLayout className="form-horizontal">
        <GridLayout.Row>
          <GridLayout.Col cols="3">
            <Label className="control-label">{popupConfig.DASHBOARD_NAME}</Label>
          </GridLayout.Col>
          <GridLayout.Col cols="9">
            <ValidatorInput
              value={model.reportName}
              className="form-control"
              shouldFocus={true}
              onChange={this.changeReportName}
              onFocus={this.onReportNameFocus}
              onKeyPress={this.onReportNameKeyPress.bind(this)}
              validations={nameValidation}
              field="reportName"
              type="text"
              name="reportName"
            />
          </GridLayout.Col>
        </GridLayout.Row>

        {this.doesShowSaveInto() && (
          <GridLayout.Row>
            <GridLayout.Col cols="3">
              <Label className="control-label">{popupConfig.SAVEINTO}</Label>
            </GridLayout.Col>
            <GridLayout.Col cols="9">
              <Select
                options={componentState.localGlobalCategories}
                labelKey="name"
                valueKey="id"
                value={selectedSaveInto}
                onChange={changeSaveInHandler}
                scalable={false}
                textboxMode={false}
              />
            </GridLayout.Col>
          </GridLayout.Row>
        )}

        <GridLayout.Row>
          <GridLayout.Col cols="3">
            <Label className="control-label">{popupConfig.CATEGORY}</Label>
          </GridLayout.Col>
          <GridLayout.Col cols="9">
            <Select
              simpleValue
              options={componentState.categories}
              labelKey="name"
              valueKey="id"
              value={selectedCategory}
              noResultsText={selectCategoryHint}
              onChange={changeCategoryHandler}
              onAddNew={addNewCategory}
              scalable={false}
              isLazyLoading
              getData={getCategories}
              onSearchLazy={searchCategoryDataHandler}
              onCloseMenu={resetCategory}
              isLastPage={componentState.isLastPageCategorySearch}
            />
          </GridLayout.Col>
        </GridLayout.Row>

        <GridLayout.Row>
          <GridLayout.Col cols="3">
            <Label className="control-label">{popupConfig.SUBCATEGORY}</Label>
          </GridLayout.Col>
          <GridLayout.Col cols="9">
            <Select
              simpleValue
              options={componentState.subCategories}
              labelKey="name"
              valueKey="id"
              value={selectedSubCategory}
              noResultsText={selectSubCategoryHint}
              onChange={changeSubCategoryHandler}
              onAddNew={addNewSubCategory}
              disabled={isNil(model.categoryId)}
              scalable={false}
              isLazyLoading
              getData={getSubCategories}
              onSearchLazy={searchSubCategoryDataHandler}
              onCloseMenu={resetSubCategory}
              isLastPage={componentState.isLastPageSubCategorySearch}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  };

  render() {
    const { localization, model, onClosing } = this.props;

    const popupTitle = !model.saveAs
      ? localization.getText('COMMON_LABEL_SAVE')
      : localization.getText('COMMON_LABEL_SAVE_AS');

    return (
      <Popup visible onClosing={onClosing} autoClose={false} type="none">
        <Popup.Header title={popupTitle} />
        <Popup.Body>{this.renderContainer()}</Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type="accept">{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

export default SavePopup;



// WEBPACK FOOTER //
// ./ui/share/components/controls/SavePopup/Izenda.SavePopup.js